import { LitElement, html, css } from 'lit';
import { findAncestor } from './base.js';

// <physics-body type="dynamic|fixed" shape="box" restitution friction density>
// Slot-wraps a three element (e.g. <skill-box> / <three-box>): turns its
// object3D into a Rapier rigid body. Registers with the nearest <rapier-physics>,
// which writes the body transform back onto the object3D every step.
export class PhysicsBody extends LitElement {
  static properties = {
    type: { type: String },
    shape: { type: String },
    restitution: { type: Number },
    friction: { type: Number },
    density: { type: Number },
  };
  static styles = css`
    :host {
      display: contents;
    }
  `;

  constructor() {
    super();
    this.type = 'dynamic';
    this.shape = 'box';
    this.restitution = 0.15;
    this.friction = 0.9;
    this.density = 1;
    this._rb = null;
    this._obj = null;
    this._phys = null;
  }

  async connectedCallback() {
    super.connectedCallback();
    const phys = findAncestor(
      this,
      (c) => c.tagName === 'RAPIER-PHYSICS' || (c.world && typeof c.register === 'function'),
    );
    if (!phys) return;
    this._phys = phys;
    if (phys.whenReady?.then) await phys.whenReady; // world ready

    const child = this._threeChild();
    if (child?.whenReady?.then) await child.whenReady; // wrapped mesh ready
    if (!this.isConnected || !child) return;
    this._obj = child.object3D;
    if (!this._obj) return;

    this._make();
    phys.register(this);
  }

  _threeChild() {
    for (const el of this.children) {
      if (el.object3D !== undefined || typeof el.whenReady?.then === 'function') return el;
    }
    return this.firstElementChild;
  }

  _make() {
    const R = this._phys.RAPIER;
    const world = this._phys.world;
    const obj = this._obj;
    obj.updateMatrixWorld();
    const p = obj.position;
    const q = obj.quaternion;
    const desc = (this.type === 'fixed' ? R.RigidBodyDesc.fixed() : R.RigidBodyDesc.dynamic())
      .setTranslation(p.x, p.y, p.z)
      .setRotation({ x: q.x, y: q.y, z: q.z, w: q.w })
      .setLinearDamping(0.05)
      .setAngularDamping(0.4);
    this._rb = world.createRigidBody(desc);
    const h = this._halfExtents(obj);
    const col = R.ColliderDesc.cuboid(h.x, h.y, h.z)
      .setRestitution(this.restitution)
      .setFriction(this.friction)
      .setDensity(this.density);
    world.createCollider(col, this._rb);

    // `flat` => coplanar centers (z-translation locked ⇒ no layering / squeeze-out), but
    // free rotation; the thin front/back walls cap how far a brick can lean into z, so you
    // get a little depth-tilt without instability.
    if (this.type !== 'fixed' && (this.hasAttribute('flat') || this._phys?.hasAttribute?.('flat'))) {
      this._rb.setEnabledTranslations(true, true, false, true);
      this._rb.setEnabledRotations(true, true, true, true);
    }
  }

  _halfExtents(obj) {
    let geom = obj.geometry;
    if (!geom && obj.children?.length) geom = obj.children.find((c) => c.geometry)?.geometry;
    if (geom) {
      geom.computeBoundingBox();
      const b = geom.boundingBox;
      const s = obj.scale;
      return {
        x: Math.max(0.05, ((b.max.x - b.min.x) / 2) * s.x),
        y: Math.max(0.05, ((b.max.y - b.min.y) / 2) * s.y),
        z: Math.max(0.05, ((b.max.z - b.min.z) / 2) * s.z),
      };
    }
    return { x: 0.5, y: 0.5, z: 0.5 };
  }

  _sync() {
    if (!this._rb || !this._obj) return;
    const t = this._rb.translation();
    this._obj.position.set(t.x, t.y, t.z);
    const r = this._rb.rotation();
    this._obj.quaternion.set(r.x, r.y, r.z, r.w);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._phys?.unregister?.(this);
    // server removed this brick (reroll) → free its rigidbody + collider from the world
    if (this._rb && this._phys?.world) {
      this._phys.world.removeRigidBody(this._rb);
      this._rb = null;
    }
  }

  render() {
    return html`<slot></slot>`;
  }
}

customElements.define('physics-body', PhysicsBody);
